import { Fragment, useCallback, useEffect, useState } from 'react';
import { CareGrid } from '@components/grid/CareGrid';
import { useGridView, type GridColumn } from '@components/grid/useGridView';
import { slicerParam, sortParam, type SlicerDef } from '@lib/grid';
import { useCampaign } from '@lib/campaigns';
import {
  DNC_KINDS, dncAdjust, dncBulk, dncList, dncRemove, stopRestart, type StopRestartResp,
  type DncBulkResp, type DncKind, type DncListResp, type DncRow, type DncSortKey, type DncStatusFilter,
} from '@lib/care-api';
import { fmtMemberId } from '@lib/member-id';
import { useFeatures, useFeaturesSettled } from '@lib/clients';
import { useStaleRead } from '@lib/stale-read';
import { carrierPlain, dialogWho, fmtDay, fmtPostalLine, howPlain, leadPlain, noMemberHasNumber, postOfficeLines, sourcePlain } from '@lib/plain-rows';
import { LOCK_FIELDSET_STYLE, StaleNote } from '@components/StaleNote';
import { TechExclusionPanel } from '@components/TechExclusionPanel';
import { OptOutReview } from '@pages/OptOutReview';
import { UspsRejections } from '@pages/UspsRejections';
import { DNC_WORDING, dncRefusalWords, dncSaidLayout, emptyNotice, emptyRow, emptyTone, fmtPhone, fmtTs, type EmptyReason } from '@lib/care-model';

/**
 * The time-out tables (nav 12), William's row 286 of 17 September 2026 and his row 300 of the same
 * afternoon:
 *
 *   286: "Make sure that the Care portal has the DNC db as a master table which marny can review
 *         and adjust and remove names from the DNC list ... then we can start to communicate."
 *   300: "Prepare a DB tables ... tabs for the following in Care: DNC, Address Issues, Phone Issues,
 *         Duplicate ... anyone in any of these tables will NOT be contacted until Marny through the
 *         Care interface moves them off ... give the user the ability to LOCK the member's condition
 *         ... and also allow for multi select so one can choose a whole bunch of people at once and
 *         then do a command on them."
 *
 * FOUR TABS, ONE TABLE. Every tab is the same full table over the same master list, which
 * ClariConnect holds; the kind decides which tab a person is on and NEVER how hard the stop is, so
 * an active row on any tab stops every channel, except a row listed FOR REVIEW (ClariConnect's 1.1
 * addendum), which blocks nothing and says so on the row. The tab counts and the status counts both
 * come out of ONE reading of the list, so they cannot disagree with each other.
 *
 * REMOVE asks for a reason, confirms in words, and then does two things the response reports
 * separately: the platform row goes inactive (texts and calls pass from that moment) and a CLEAR
 * lands in the care decisions record (the file notation clears at the next Portal build). Nothing
 * is deleted; a removed row stays on the list under "Removed" with who, when and why.
 * LOCK is his guard against an accidental return: a locked row cannot be removed until someone
 * unlocks it and says why. It changes no enforcement, so it sits behind the screen's switch alone.
 * ADJUST edits the member id, the name or a note; it changes nothing about who may be contacted.
 *
 * SORTED AND PAGED SERVER-SIDE, like every list here since the Archive page learned why.
 */

const PAGE = 60;
/** The keys the list route sorts by (row 356: up to three of them, Shift-click on a heading). */
const SORT_KEYS: readonly DncSortKey[] = ['member', 'phone', 'when', 'method', 'status', 'added'];
/**
 * Column shares after the checkbox: Member, Phone, How, the substance (Why on the list / Address /
 * What is wrong), Source, When, Added, On their file, Status, Action. `null` takes what is left.
 * Since rows 355 to 359 the substance takes the rest on EVERY tab: Source is now a short plain line
 * (the evidence went under Technical detail), while an address, a shared phone's member table or a
 * basis sentence needs the width. A narrow substance column wrapped the member table letter by letter.
 */
function colWidths(kind: DncKind): ReadonlyArray<string | null> {
  return kind === 'dnc' || kind === 'optout'
    ? ['10%', '8%', '8%', null, '12%', '6%', '8%', '7%', '10%', '13%']
    : ['10%', '8%', '8%', null, '10%', '6%', '6%', '7%', '9%', '12%'];
}

const METHOD_LABEL: Record<DncRow['method'], string> = {
  sms: 'Text',
  voice: 'Automated call',
  import: 'From an earlier list',
  care: 'Care admin',
  'care-portal': 'Care portal',
  other: 'Other',
};

type FileAnswer = 'noMember' | 'fileUnknown' | 'onFileClears' | 'onFile' | 'notOnFile';
const FILE_TONE: Record<FileAnswer, '' | 'muted' | 'warn'> = { noMember: 'muted', fileUnknown: 'warn', onFileClears: 'warn', onFile: '', notOnFile: 'muted' };
/** The legend's order: the commonest answers first. */
const FILE_ANSWERS: readonly FileAnswer[] = ['onFile', 'onFileClears', 'notOnFile', 'noMember', 'fileUnknown'];

/**
 * What the "On their file" cell says, from the row's notation and the console's own decision. ONE
 * answer, read as a few words in the cell and as the full sentence in its tooltip and the legend,
 * so the two cannot disagree.
 */
export function fileWording(r: DncRow, rosterAvailable: boolean): { text: string; short: string; tone: '' | 'muted' | 'warn' } {
  const said = (a: FileAnswer) => ({ text: DNC_WORDING[a], short: DNC_WORDING.fileShort[a], tone: FILE_TONE[a] });
  if (!r.memberExternalId) return said('noMember');
  if (!rosterAvailable) return said('fileUnknown');
  if (!r.notation) return said('noMember');
  if (r.notation.onFile) {
    // A CLEAR already sent from here means the notation is on its way off the file.
    if (r.careDecision?.decision === 'CLEAR' || !r.active) return said('onFileClears');
    return said('onFile');
  }
  return said('notOnFile');
}

/**
 * ROW 355 / 357 / 359: everything stored with a row that is NOT plain data for the operator (the file
 * and spreadsheet row it was loaded from, the check's raw words, import notes, the change history with
 * its reasons), kept whole and shown only to the owner, folded under one link.
 */
function TechnicalDetail({ r }: { r: DncRow }): JSX.Element | null {
  const lines: string[] = [];
  if (r.reason) lines.push(`reason: ${r.reason}`);
  if (r.responseType) lines.push(`reply type: ${r.responseType}`);
  if (r.seeded && r.response) lines.push(`response: ${r.response}`);
  if (r.primaryAction && r.primaryAction !== 'STOP') lines.push(`action: ${r.primaryAction}`);
  if (r.evidence) {
    const e = r.evidence;
    lines.push(`evidence: ${[e.check, e.detail, e.file ? `${e.file}${e.row ? ` row ${e.row}` : ''}` : ''].filter(Boolean).join(' | ')}`);
  }
  for (const e of r.alsoEvidence ?? []) lines.push(`also: ${[e.check || e.detail, e.file ? `${e.file}${e.row ? ` row ${e.row}` : ''}` : ''].filter(Boolean).join(' | ')}`);
  if (r.source) lines.push(`source: ${r.source}${r.workbookRows.length ? ` (row ${r.workbookRows.join(', ')})` : ''}`);
  if (r.addedBy) lines.push(`added by: ${r.addedBy}`);
  if (r.note) lines.push(`note: ${r.note}`);
  if (r.memberFrom) lines.push(`member from: ${r.memberFrom}`);
  if (r.notation?.optoutClass) lines.push(`record: ${r.notation.optoutClass}${r.notation.optoutAt ? ` at ${fmtTs(r.notation.optoutAt)}` : ''}`);
  if (r.basisSource || r.basisDate) lines.push(`basis: ${[r.basisSource, r.basisDate].filter(Boolean).join(', ')}`);
  for (const h of r.history) lines.push(`${fmtTs(h.at)} ${h.verb} by ${h.by}${h.reason ? `: ${h.reason}` : ''}`);
  for (const l of r.dialog) if (l.direction === 'carrier') lines.push(`carrier ${l.at ? fmtTs(l.at) : ''}: ${l.text}`);
  if (!lines.length) return null;
  return (
    <details className="small muted" style={{ marginTop: 4 }} data-testid="technical-detail">
      <summary>{DNC_WORDING.technicalDetail}</summary>
      {lines.map((l, i) => <div key={i} style={{ overflowWrap: 'anywhere' }}>{l}</div>)}
    </details>
  );
}

function who(r: DncRow): string {
  return r.rosterName || r.memberName || (r.memberExternalId ? fmtMemberId(r.memberExternalId) : r.membershipMemberId ? fmtMemberId(r.membershipMemberId) : 'this number');
}

/**
 * ROW 356: the slicers this list offers, answered by the SERVER over the whole list (the screen holds
 * one page). Each is a field the row carries: how the stop arrived, what it blocks, whether it stops
 * anything at all, and whether a member is tied to it.
 */
const DNC_SLICER_DEFS: SlicerDef<DncRow>[] = [
  { key: 'how', label: 'How', value: (r) => r.method, labelFor: (v) => METHOD_LABEL[v as DncRow['method']] ?? v },
  { key: 'channel', label: 'Blocks', value: (r) => r.channel, labelFor: (v) => DNC_WORDING.channelWords(v as DncRow['channel']) },
  { key: 'review', label: 'Stops', value: (r) => (r.enforced ? 'enforced' : 'review'), labelFor: (v) => (v === 'review' ? `${DNC_WORDING.reviewPill}: ${DNC_WORDING.reviewShort}` : 'Every channel') },
  { key: 'member', label: 'Member', value: () => null, labelFor: (v) => (v === 'tied' ? 'On file' : v === 'membership' ? DNC_WORDING.fromMembershipFile : 'No member') },
];

/** What one row's cells need from the list around them. */
interface DncCellCtx {
  kind: DncKind;
  superAdmin: boolean;
  rosterAvailable: boolean;
  platformWrite: boolean;
  selected: ReadonlySet<string>;
  allOnPage: boolean;
  onSelectPage: (on: boolean) => void;
  toggleRow: (id: string) => void;
  editing: string;
  draft: { memberExternalId: string; memberName: string; note: string };
  setDraft: (d: { memberExternalId: string; memberName: string; note: string }) => void;
  reasons: Record<string, string>;
  setReason: (id: string, v: string) => void;
  busy: string;
  pendingIds: Record<string, string>;
  remove: (r: DncRow) => void;
  /** Row 578a (R. 128): shown only when MEMBERCARE_STOP_RESTART is on (the clients read says so). */
  stopRestartOn: boolean;
  restart: (r: DncRow) => void;
  adjust: (r: DncRow) => void;
  openAdjust: (r: DncRow) => void;
  cancelAdjust: () => void;
}

/**
 * ROW 356: the list's eleven columns for the shared table, keyed by the route's sort keys where a
 * heading sorts. Every cell below is the cell rows 355 to 359 built, moved here unchanged: plain words
 * from the row's FIELDS, the stored text that loaded the row only under Technical detail. The planned
 * widths (walk finding 5) ride each column; the substance takes the rest.
 */
function dncColumns(c: DncCellCtx): GridColumn<DncRow>[] {
  const w = colWidths(c.kind);
  const pct = (i: number) => w[i] ?? undefined;
  return [
    {
      key: 'select', menuLabel: 'Tick box', locked: true, defaultWidth: 36, thStyle: { width: 28 },
      label: (
        <input
          type="checkbox"
          aria-label="Select every row on this page"
          checked={c.allOnPage}
          onChange={(e) => c.onSelectPage(e.target.checked)}
        />
      ),
      render: (r) => (
        <input
          type="checkbox"
          aria-label={`Select ${who(r)}`}
          checked={c.selected.has(r.dncId)}
          onChange={() => c.toggleRow(r.dncId)}
        />
      ),
    },
    {
      key: 'member', label: 'Member', defaultWidth: pct(0), sortValue: () => null,
      render: (r) => {
        const noMember = noMemberHasNumber(r);
        return (
          <>
            <div>{r.rosterName || r.memberName || (noMember ? <span className="muted">{DNC_WORDING.noMemberHasNumber}</span> : <span className="muted">No name on file</span>)}</div>
            <div className="small muted">
              {r.memberExternalId
                ? fmtMemberId(r.memberExternalId)
                : r.membershipMemberId
                  ? `${fmtMemberId(r.membershipMemberId)}, ${DNC_WORDING.fromMembershipFile}`
                  : (r.rosterName || r.memberName) && noMember ? DNC_WORDING.noMemberHasNumber : ''}
              {r.rosterName && r.memberName && r.rosterName.toUpperCase() !== r.memberName.toUpperCase() ? ` (list says ${r.memberName})` : ''}
            </div>
          </>
        );
      },
    },
    {
      // A member-keyed row (an address or duplicate issue) holds no phone by design.
      key: 'phone', label: 'Phone', defaultWidth: pct(1), tdClassName: 'small', sortValue: () => null,
      render: (r) => (r.phone ? fmtPhone(r.phone) : <span className="muted">No phone</span>),
    },
    {
      key: 'method', label: 'How', defaultWidth: pct(2), sortValue: () => null,
      render: (r) => (
        <>
          <div>{howPlain(r, METHOD_LABEL[r.method])}</div>
          <div className="small muted">{DNC_WORDING.channelWords(r.channel)}</div>
          {/* Row 325: the ONE campaign an opt-out stops, and what a technical row is about. */}
          {r.kind === 'optout' && <div className="small">{r.campaign ? `Opt-out of ${r.campaign.toUpperCase()} only` : 'Opt-out: no campaign named'}</div>}
          {r.issue && r.kind !== 'optout' && r.kind !== 'delivery' && <div className="small muted">{DNC_WORDING.issueLabel[r.issue] ?? r.issue}</div>}
        </>
      ),
    },
    {
      key: 'why', label: DNC_WORDING.whyHeading[c.kind] ?? 'What they said', menuLabel: DNC_WORDING.whyHeading[c.kind] ?? 'What they said',
      render: (r) => {
        const said = dncSaidLayout(r);
        const postalRow = r.kind === 'delivery' && (r.issue ?? 'address') === 'address';
        const po = postOfficeLines(r.postal ?? null, r.reason);
        const sharedPhone = !postalRow && !!r.members && r.members.length > 1;
        return (
          <>
            {postalRow ? (
              /* ROW 357: what the plan gave beside what the Post Office says, then what to do. */
              <div style={{ display: 'grid', gap: 2 }} data-testid="postal-lines">
                {r.postal?.plan && <div><span className="muted">Plan gave: </span>{fmtPostalLine(r.postal.plan)}</div>}
                {po.address && <div><span className="muted">Post Office: </span>{po.address}</div>}
                {po.sentences.map((t) => <div key={t}>{t}</div>)}
                <div className="small"><span className="muted">What to do: </span>{DNC_WORDING.confirmAddress}</div>
              </div>
            ) : sharedPhone ? (
              /* ROW 359 A: said once, then the evidence as a small table, then what it means. */
              <div style={{ display: 'grid', gap: 4 }} data-testid="shared-phone">
                <div>{DNC_WORDING.sharedBy(r.members!.length)}</div>
                <table className="dt dt-mini">
                  <thead><tr><th>Member</th><th>Member ID</th><th>Campaign</th></tr></thead>
                  <tbody>
                    {r.members!.map((m, i) => (
                      <tr key={`${m.memberExternalId}-${i}`}>
                        <td>{m.memberName || <span className="muted">No name</span>}</td>
                        <td>{m.memberExternalId ? fmtMemberId(m.memberExternalId) : ''}</td>
                        <td>{m.campaigns.map((c2) => c2.toUpperCase()).join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="small">{DNC_WORDING.sharedMeans}</div>
                <div className="small"><span className="muted">What to do: </span>{DNC_WORDING.sharedDo}</div>
              </div>
            ) : r.basis ? (
              /* ROW 358 point 5: why the number is on the list, from the platform's basis. */
              <div style={{ display: 'grid', gap: 2 }} data-testid="basis">
                <div>{r.basis}</div>
                {r.basisWords && <div className="small"><q>{r.basisWords}</q></div>}
              </div>
            ) : (
              <div>
                {said.lead && leadPlain(said.lead) && <div>{leadPlain(said.lead)}</div>}
                {r.ourMessage && <div className="small muted">We sent{r.ourMessage.at ? ` ${fmtTs(r.ourMessage.at)}` : ''}: {r.ourMessage.text}</div>}
                {said.lead || r.dialog.length ? null : r.response ? <q>{r.response}</q> : <span className="muted">Not recorded</span>}
              </div>
            )}
            {/* ROW 359 A: a text history sits behind a click, newest first, never a wall of messages. */}
            {r.dialog.length > 0 && (
              <details className="small" style={{ marginTop: 4 }}>
                <summary>{DNC_WORDING.showMessages(r.dialog.length)}</summary>
                <div style={{ display: 'grid', gap: 2, marginTop: 4 }}>
                  {[...r.dialog].reverse().map((l, i) => (
                    <div key={`${l.at}-${i}`}>
                      <span className="muted">{l.at ? `${fmtTs(l.at)}, ` : ''}{dialogWho(l.direction)}: </span>
                      {l.direction === 'carrier' ? carrierPlain(l.text) : l.direction === 'in' ? <q>{l.text}</q> : l.text}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </>
        );
      },
    },
    {
      key: 'source', label: 'Source', defaultWidth: pct(4), tdClassName: 'small',
      render: (r) => {
        const superAdmin = c.superAdmin;
        return (
          <>
            <div>{sourcePlain(r, r.postal ?? null)}</div>
            {superAdmin && <TechnicalDetail r={r} />}
          </>
        );
      },
    },
    {
      // Row 358: the day the member stopped, from the basis, before the day the row was loaded.
      key: 'when', label: 'When', defaultWidth: pct(5), tdClassName: 'small', firstDir: 'desc', sortValue: () => null,
      render: (r) => <>{r.basisDate ? fmtDay(r.basisDate) : r.receivedAt ? fmtTs(r.receivedAt) : <span className="muted">No date</span>}</>,
    },
    {
      key: 'added', label: 'Added', defaultWidth: pct(6), tdClassName: 'small', firstDir: 'desc', sortValue: () => null,
      render: (r) => (r.addedAt ? fmtTs(r.addedAt) : '-'),
    },
    {
      key: 'file', label: 'On their file', defaultWidth: pct(7),
      render: (r) => {
        const fw = fileWording(r, c.rosterAvailable);
        const noMember = noMemberHasNumber(r);
        return (
          <div title={fw.text}>
            <div className={fw.tone === 'muted' ? 'small muted' : 'small'}>{noMember ? '-' : fw.short}</div>
            {r.careDecision && <div className="small muted">Decision: {r.careDecision.decision.toLowerCase()} by {r.careDecision.by} {fmtTs(r.careDecision.at)}</div>}
          </div>
        );
      },
    },
    {
      key: 'status', label: 'Status', defaultWidth: pct(8), sortValue: () => null,
      render: (r) => (
        <>
          {r.active ? (
            <span className="pill pill-danger">On the list</span>
          ) : (
            <div className="small">
              <span className="pill pill-success">Removed</span>
              <div className="muted">{r.removedBy ?? ''} {r.removedAt ? fmtTs(r.removedAt) : ''}</div>
              {r.removedReason && <div className="muted">{r.removedReason}</div>}
            </div>
          )}
          {/* A REVIEW row blocks nothing; without this the row reads like every other stop. */}
          {!r.enforced && (
            <div className="small" style={{ marginTop: 4 }} title={DNC_WORDING.reviewNote}>
              <span className="pill pill-accent">{DNC_WORDING.reviewPill}</span>
              <div className="muted">{DNC_WORDING.reviewShort}</div>
            </div>
          )}
          {/* The lock, and WHO set it and WHY: an admin about to unlock has to be able to
              read the reason the row was locked before she overrides it. */}
          {r.locked && (
            <div className="small" style={{ marginTop: 4 }}>
              <span className="pill pill-warning">Locked</span>
              <div className="muted">{DNC_WORDING.lockedNote(r.lockedBy, r.lockedAt ? fmtTs(r.lockedAt) : '', r.lockReason)}</div>
            </div>
          )}
        </>
      ),
    },
    {
      key: 'action', label: 'Action', locked: true, defaultWidth: pct(9),
      render: (r) => {
        const isEditing = c.editing === r.dncId;
        const { draft, setDraft } = c;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {isEditing && (
              <div style={{ display: 'grid', gap: 4 }}>
                <input className="text-input" aria-label="Member id" placeholder="Member id (blank to untie)" value={draft.memberExternalId} onChange={(e) => setDraft({ ...draft, memberExternalId: e.target.value })} />
                <input className="text-input" aria-label="Member name" placeholder="Member name" value={draft.memberName} onChange={(e) => setDraft({ ...draft, memberName: e.target.value })} />
                <input className="text-input" aria-label="Note" placeholder="Note (optional)" value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
              </div>
            )}
            <input
              className="text-input"
              aria-label={`Reason for ${who(r)}`}
              placeholder="Reason"
              title="Reason (required, at least 10 characters)"
              value={c.reasons[r.dncId] ?? ''}
              onChange={(e) => c.setReason(r.dncId, e.target.value)}
            />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {r.active && (
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  disabled={c.busy === r.dncId || !c.platformWrite || r.locked}
                  title={r.locked
                    ? DNC_WORDING.lockedNote(r.lockedBy, r.lockedAt ? fmtTs(r.lockedAt) : '', r.lockReason)
                    : !c.platformWrite ? DNC_WORDING.platformOff
                      : r.enforced ? 'Removes the name from the list: texts and calls pass from now, the file notation clears at the next build.'
                        : DNC_WORDING.removeTitleReview}
                  onClick={() => void c.remove(r)}
                >
                  {c.pendingIds[r.dncId] ? 'Retry remove' : 'Remove'}
                </button>
              )}
              {c.stopRestartOn && r.active && r.kind === 'dnc' && !!r.memberExternalId && (
                <button
                  type="button"
                  className="btn btn-sm"
                  data-testid="stop-restart"
                  disabled={c.busy === r.dncId || !c.platformWrite}
                  title={!c.platformWrite ? DNC_WORDING.platformOff : RESTART_WORDING.title}
                  onClick={() => void c.restart(r)}
                >
                  {c.pendingIds[`restart:${r.memberExternalId}`] ? 'Retry restart' : 'Restart after STOP'}
                </button>
              )}
              {!isEditing ? (
                <button type="button" className="btn btn-sm btn-ghost" disabled={c.busy === r.dncId} onClick={() => c.openAdjust(r)}>Adjust</button>
              ) : (
                <>
                  <button type="button" className="btn btn-sm" disabled={c.busy === r.dncId} onClick={() => void c.adjust(r)}>Save adjustment</button>
                  <button type="button" className="btn btn-sm btn-ghost" disabled={c.busy === r.dncId} onClick={() => c.cancelAdjust()}>Cancel</button>
                </>
              )}
            </div>
          </div>
        );
      },
    },
  ];
}

/** One batch answer in words: what landed, what was refused, and by whom it was refused. */
/** Row 578a: the button's words, in one place. */
export const RESTART_WORDING = {
  title: 'Lifts this member\'s STOP on every phone, notes the member\'s file (the Client sees it at the next build), and tells you to ask the member to text START.',
  confirm: (name: string) => `Restart after STOP for ${name}? This lifts the member's STOP on every phone and records it in the member's file. The phone company's own block stays until the member texts START.`,
};

/** Row 578a: what the operator reads after a restart: what was lifted, then what she must do. */
export function restartSentence(name: string, d: { removed: string[]; notRemoved: Array<{ dncId: string; reason: string }>; askMember: string; carrierStillOptedOut: boolean | null; clientNote: string }): string {
  const n = d.removed.length;
  const head = `STOP lifted for ${name} (${n} row${n === 1 ? '' : 's'}).`;
  const left = d.notRemoved.length ? ` ${d.notRemoved.length} row${d.notRemoved.length === 1 ? '' : 's'} could not be lifted: ${d.notRemoved.map((x) => x.reason).join(', ')}.` : '';
  const carrier = d.carrierStillOptedOut === true ? ' The phone company still holds its block on that phone.' : '';
  return `${head}${left}${carrier} ${d.askMember} ${d.clientNote}`;
}

/**
 * THE OFFICE'S 197DBA4A (22 Oct 2026, option a): with MEMBERCARE_UNSTOP_CARE_LINE on, the server refuses to remove (or
 * move to an opt-out) a CARE STOP row and names the two lifts that count. The sentence is the server's (lockstep test).
 */
export const CARE_STOP_WORDS = 'This member\'s STOP is lifted only by Restart after STOP or Unstop';
export type CareStopLink = { label: string; href: string };
/** The links to show after a refusal: Restart after STOP once, then Unstop for each member named. Pure. */
export function careStopLinksOf(d: { links?: CareStopLink[]; careStop?: { members?: Array<{ memberId: string; links?: CareStopLink[] }> } }): CareStopLink[] {
  const all = [...(d.links ?? []), ...(d.careStop?.members ?? []).flatMap((m) => (m.links ?? []).map((l) => (l.label === 'Unstop' ? { ...l, label: `Unstop #${fmtMemberId(m.memberId)}` } : l)))];
  const seen = new Set<string>();
  return all.filter((l) => (seen.has(l.href) ? false : (seen.add(l.href), true)));
}

export function bulkSentence(d: DncBulkResp): string {
  const s = d.summary ?? { selected: 0, sent: 0, done: 0, refused: 0 };
  const head = `${s.done} of ${s.selected} row${s.selected === 1 ? '' : 's'} ${d.op === 'mark' ? 'marked' : d.op === 'move' ? 'moved' : d.op === 'remove' ? 'removed' : d.op === 'lock' ? 'locked' : 'unlocked'}`;
  if (!s.refused) return `${head}.${d.fileNote ? ` ${d.fileNote}.` : ''}`;
  // The reasons, grouped, so twenty refusals of one kind read as one sentence and not as twenty.
  const byReason = new Map<string, number>();
  for (const r of d.results ?? []) {
    if (r.status !== 'refused') continue;
    const key = r.reason || 'refused without a named reason';
    byReason.set(key, (byReason.get(key) ?? 0) + 1);
  }
  const why = [...byReason.entries()].map(([k, n]) => `${n} ${k === 'care_stop' ? `${CARE_STOP_WORDS} (care_stop)` : dncRefusalWords(k)}`).join('; ');
  return `${head}; ${s.refused} refused (${why}).${d.fileNote ? ` ${d.fileNote}.` : ''}${carrierTail(d)}`;
}

/**
 * The carrier's word on a batch removal, which the single-row Remove has always shown and a batch
 * used to swallow. A removal the carrier still holds means the text BOUNCES, so an operator who is
 * told only "12 removed" would believe twelve people are now reachable when some are not.
 */
function carrierTail(d: DncBulkResp): string {
  if (d.op !== 'remove' && d.op !== 'move') return '';
  const held = (d.results ?? []).filter((r) => r.carrierStillOptedOut === true).length;
  const unknown = (d.results ?? []).filter((r) => r.carrierCheck === 'unavailable').length;
  if (!held && !unknown) return '';
  const parts: string[] = [];
  if (held) parts.push(`${held} still held by the carrier, so texts to them bounce until the member texts START to the 833 number`);
  if (unknown) parts.push(`${unknown} the carrier could not be asked about (the removal still stands)`);
  return ` ${parts.join('; ')}.`;
}

/** Row 327's campaign choice now IS the top-bar CAMPAIGN menu (row 354): one selection for the console. */

/** The first list that is not `kind`, which is where a Move or a Mark most likely goes. */
function otherKind(kind: DncKind): DncKind {
  return DNC_KINDS.find((k) => k !== kind) ?? 'dnc';
}

/**
 * ONE exceptions list (row 325): the rows of one kind with Remove, Move, Lock and Mark, the locked
 * rows in their own view, and the campaign selector of row 327 on top. Every nav tab from 10 to 13
 * renders this with its own kind, so the four lists cannot drift apart.
 */
export function ExceptionsList({ kind }: { kind: DncKind }): JSX.Element {
  const [status, setStatus] = useState<DncStatusFilter>('active');
  /**
   * ROW 327. '' is "not chosen yet": the first read asks for every campaign, and the platform's
   * default then applies. 'all' is the operator's explicit "All campaigns".
   */
  const campaign = useCampaign();
  const [issue, setIssue] = useState('');
  const [q, setQ] = useState('');
  const [typed, setTyped] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<DncListResp | null>(null);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  /** 197DBA4A: the two lifts a refused care STOP points to (empty: nothing shown, today's screen). */
  const [careLinks, setCareLinks] = useState<CareStopLink[]>([]);
  const [busy, setBusy] = useState('');
  const [reasons, setReasons] = useState<Record<string, string>>({});
  /** The row whose adjust form is open, and its draft. One at a time. */
  const [editing, setEditing] = useState('');
  const [draft, setDraft] = useState<{ memberExternalId: string; memberName: string; note: string }>({ memberExternalId: '', memberName: '', note: '' });
  /** An attempt whose outcome was unknown keeps its id, so the retry is a replay and never a second write. */
  const [pendingIds, setPendingIds] = useState<Record<string, string>>({});
  /** The multi-select and its one command-bar reason. */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkReason, setBulkReason] = useState('');
  const [markKind, setMarkKind] = useState<DncKind>(() => otherKind(kind));
  const [markLock, setMarkLock] = useState(false);
  /** Row 325's Move: the list the rows land on, and the campaign when that list is an opt-out. */
  const [moveTo, setMoveTo] = useState<DncKind>(() => otherKind(kind));
  const [moveCampaign, setMoveCampaign] = useState('');
  /** A batch whose answer never arrived keeps its id: the retry replays and writes nothing twice. */
  const [pendingBatch, setPendingBatch] = useState('');
  /** Row 372: tab 13's "Add a member to technical exclusion" panel, dark behind its own switch. */
  const features = useFeatures();

  // A new campaign in the top bar starts the list from its first page.
  useEffect(() => { setOffset(0); }, [campaign]);

  /**
   * ROW 356: the shared table. Its sort (up to three keys) and slicers are the operator's, remembered
   * per list, and both go to the SERVER, because this screen holds one page of the list.
   */
  const pageRows = data?.rows ?? [];
  const pageIdsNow = pageRows.map((r) => r.dncId);
  const columns = dncColumns({
    kind,
    superAdmin: data?.superAdmin === true,
    rosterAvailable: data?.rosterAvailable !== false,
    platformWrite: data?.platformWrite === true,
    selected,
    allOnPage: pageIdsNow.length > 0 && pageIdsNow.every((id) => selected.has(id)),
    onSelectPage: (on) => setSelected((s) => {
      const n = new Set(s);
      for (const id of pageIdsNow) { if (on) n.add(id); else n.delete(id); }
      return n;
    }),
    toggleRow: (id) => toggleRow(id),
    editing,
    draft,
    setDraft,
    reasons,
    setReason: (id, v) => setReasons((s) => ({ ...s, [id]: v })),
    busy,
    pendingIds,
    remove: (r) => void remove(r),
    stopRestartOn: features.stopRestart,
    restart: (r) => void restart(r),
    adjust: (r) => void adjust(r),
    openAdjust: (r) => openAdjust(r),
    cancelAdjust: () => setEditing(''),
  });
  const view = useGridView(`exceptions-${kind}`, columns, {
    defaultSort: [{ k: 'when', asc: false }],
    slicers: DNC_SLICER_DEFS,
    serverSortable: SORT_KEYS,
  });
  const sortWire = sortParam(view.sort);
  const sliceWire = slicerParam(view.slicers);
  // A new sort or slicer starts from the first page, as a new sort always did.
  useEffect(() => { setOffset(0); }, [sortWire, sliceWire]);

  /**
   * Row 359 B: the view the rows on screen belong to, and when a re-read of THAT view failed. A failed
   * read of a NEW view (another status, campaign, search, sort or page) still shows NO rows, because
   * the old rows would stand under the new view's name. A failed RE-read of the view already on screen
   * (after a write, or Try again) keeps its rows instead of emptying the table, which collapsed the page
   * and threw the reader to the top; the rows sit under a red line and every action is switched off
   * until a read succeeds, so nobody acts on a stale list about who may be contacted.
   */
  const viewKey = JSON.stringify([status, kind, campaign, issue, q, sortWire, sliceWire, offset]);
  const { staleAt, ok: readOk, failed: readFailed } = useStaleRead();

  const load = useCallback(async () => {
    setErr('');
    try {
      setData(await dncList({
        status, kind, q, sort: sortWire, limit: PAGE, offset,
        ...(sliceWire ? { slice: sliceWire } : {}),
        ...(campaign && campaign !== 'all' ? { campaign } : {}),
        ...(issue ? { issue } : {}),
      }));
      readOk(viewKey);
    } catch (e) {
      // A failed read of a new view shows an error and NO rows, never another view's list.
      if (!readFailed(viewKey)) setData(null);
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [status, kind, campaign, issue, q, sortWire, sliceWire, offset, viewKey, readOk, readFailed]);

  useEffect(() => { void load(); }, [load]);

  const campaigns = data?.campaigns ?? [];

  // A SELECTION NEVER SURVIVES A CHANGE OF VIEW. Rows selected on one view are not on screen after a
  // status, campaign, issue, search or page change, and a command bar acting on rows the operator can
  // no longer see is the one way this screen could do something nobody asked for.
  useEffect(() => { setSelected(new Set()); setPendingBatch(''); }, [kind, status, campaign, issue, q, offset, sortWire, sliceWire]);

  function openAdjust(r: DncRow): void {
    setEditing(r.dncId);
    setDraft({ memberExternalId: r.memberExternalId ?? '', memberName: r.memberName ?? '', note: '' });
  }

  /**
   * ROW 578a (R. 128 "yes."): restart after STOP, ONE press for the member. The server lifts every
   * active STOP row of the member (unlocking first), writes the CLEAR to the member's file (which is
   * how the Client is told), and answers the sentence Care must act on: ask the member to text START.
   */
  async function restart(r: DncRow): Promise<void> {
    const memberId = String(r.memberExternalId ?? '');
    const key = `restart:${memberId}`;
    const reason = (reasons[r.dncId] ?? '').trim();
    setOk('');
    if (reason.length < 10) { setErr(DNC_WORDING.reasonRequired); return; }
    if (!window.confirm(RESTART_WORDING.confirm(who(r)))) return;
    setBusy(r.dncId);
    setErr('');
    try {
      const res = await stopRestart(memberId, reason, pendingIds[key]);
      const d = res.data;
      if (!res.ok) {
        // A replayable failure keeps its id, so the next press replays rather than writes twice.
        if (d?.replayable && d.clientRequestId) setPendingIds((s) => ({ ...s, [key]: d.clientRequestId as string }));
        setErr(String(d?.error ?? `Request failed (${res.status})`));
        return;
      }
      setOk(restartSentence(who(r), d as StopRestartResp));
      setReasons((s) => ({ ...s, [r.dncId]: '' }));
      setPendingIds((s) => { const n = { ...s }; delete n[key]; return n; });
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy('');
    }
  }

  async function remove(r: DncRow): Promise<void> {
    const reason = (reasons[r.dncId] ?? '').trim();
    setOk('');
    if (reason.length < 10) { setErr(DNC_WORDING.reasonRequired); return; }
    const phoneWords = r.phone ? fmtPhone(r.phone) : 'no phone';
    if (!window.confirm(r.enforced ? DNC_WORDING.removeConfirm(who(r), phoneWords) : DNC_WORDING.removeConfirmReview(who(r), phoneWords))) return;
    setBusy(r.dncId);
    setErr('');
    setCareLinks([]);
    try {
      const res = await dncRemove(r.dncId, reason, pendingIds[r.dncId]);
      const d = res.data as { error?: unknown; status?: unknown; directWrite?: { platform?: { status?: string; clientRequestId?: string; reason?: string } }; carrierStillOptedOut?: boolean | null; reason?: string };
      if (!res.ok) {
        if (res.status === 409 && d.status === 'platform_write_off') setErr(DNC_WORDING.platformOff);
        else setErr(String(d.error ?? `Request failed (${res.status})`));
        return;
      }
      const p = d.directWrite?.platform;
      if (d.status === 'refused' && d.reason === 'already_removed') {
        setOk('Already removed; nothing was sent.');
      } else if (d.status === 'refused' && d.reason === 'care_stop') {
        setErr(CARE_STOP_WORDS);
        setCareLinks(careStopLinksOf(d as { links?: CareStopLink[] }));
      } else if (d.status === 'refused' && d.reason === 'locked') {
        setErr(DNC_WORDING.lockedRefusal);
      } else if (p?.status === 'done') {
        setOk(!r.enforced ? DNC_WORDING.removedReview : d.carrierStillOptedOut === true ? DNC_WORDING.removedCarrierHolds : DNC_WORDING.removed);
        setReasons((s) => ({ ...s, [r.dncId]: '' }));
        setPendingIds((s) => { const n = { ...s }; delete n[r.dncId]; return n; });
      } else if (p?.status === 'unknown') {
        // Keep the id: the next click replays it and the platform writes nothing twice.
        if (p.clientRequestId) setPendingIds((s) => ({ ...s, [r.dncId]: p.clientRequestId as string }));
        setErr(`The platform did not answer, so it is not known whether the removal was written (${p.reason}). Click Remove again to retry safely.`);
      } else {
        setErr(`The removal did not reach the master list: ${p?.reason || p?.status || 'not attempted'}. The member stays on the list.`);
      }
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy('');
    }
  }

  async function adjust(r: DncRow): Promise<void> {
    const reason = (reasons[r.dncId] ?? '').trim();
    setOk('');
    if (reason.length < 10) { setErr(DNC_WORDING.reasonRequired); return; }
    const patch: { memberExternalId?: string | null; memberName?: string | null; note?: string } = {};
    const id = draft.memberExternalId.trim();
    const name = draft.memberName.trim();
    if (id !== (r.memberExternalId ?? '')) patch.memberExternalId = id || null;
    if (name !== (r.memberName ?? '')) patch.memberName = name || null;
    if (draft.note.trim()) patch.note = draft.note.trim();
    if (Object.keys(patch).length === 0) { setErr('Nothing changed: edit the member id, the name, or add a note.'); return; }
    setBusy(r.dncId);
    setErr('');
    try {
      const res = await dncAdjust(r.dncId, reason, patch);
      if (!res.ok) { setErr(String((res.data as { error?: unknown }).error ?? `Request failed (${res.status})`)); return; }
      const d = res.data as { status?: string; reason?: string };
      if (d.status === 'refused') { setErr(`The platform refused the adjustment: ${d.reason ?? 'no reason given'}. Nothing was changed.`); return; }
      setOk('Adjusted. The change is on the master list with your reason.');
      setReasons((s) => ({ ...s, [r.dncId]: '' }));
      setEditing('');
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy('');
    }
  }

  /** The command bar. One reason for the batch, one confirmation in words, one id kept on an unknown. */
  async function runBulk(op: 'remove' | 'lock' | 'unlock' | 'mark' | 'move'): Promise<void> {
    setOk('');
    const ids = [...selected];
    if (!ids.length) { setErr(DNC_WORDING.noSelection); return; }
    const reason = bulkReason.trim();
    if (reason.length < 10) { setErr(DNC_WORDING.reasonRequired); return; }
    // An opt-out names ONE campaign, on a mark and a move alike; asked before anything is sent.
    const target = op === 'mark' ? markKind : op === 'move' ? moveTo : kind;
    const targetCampaign = target === 'optout' && (op === 'mark' || op === 'move') ? moveCampaign : '';
    if (target === 'optout' && (op === 'mark' || op === 'move') && !targetCampaign) {
      setErr(campaigns.length ? DNC_WORDING.pickCampaign : DNC_WORDING.noCampaigns);
      return;
    }
    const listWord = DNC_WORDING.kindLabel[target] ?? target;
    const confirm = op === 'remove' ? DNC_WORDING.bulkRemoveConfirm(ids.length, listWord)
      : op === 'lock' ? DNC_WORDING.lockConfirm(ids.length)
        : op === 'unlock' ? DNC_WORDING.unlockConfirm(ids.length)
          : op === 'move' ? DNC_WORDING.moveConfirm(ids.length, DNC_WORDING.kindLabel[kind] ?? kind, listWord, targetCampaign ? campaignName(targetCampaign) : null)
            : DNC_WORDING.bulkMarkConfirm(ids.length, `${listWord}${targetCampaign ? ` for ${campaignName(targetCampaign)}` : ''}`, 'you', markLock);
    if (!window.confirm(confirm)) return;
    setBusy('bulk');
    setErr('');
    setCareLinks([]);
    try {
      const res = await dncBulk({
        op,
        dncIds: ids,
        reason,
        ...(op === 'mark' ? { kind: markKind, lock: markLock } : {}),
        ...(op === 'move' ? { toKind: moveTo } : {}),
        ...(targetCampaign ? { campaign: targetCampaign } : {}),
        ...(pendingBatch ? { clientRequestId: pendingBatch } : {}),
      });
      if (!res.ok) {
        const e = res.data as { error?: unknown; status?: unknown };
        if (res.status === 409 && e.status === 'platform_write_off') setErr(DNC_WORDING.platformOff);
        else setErr(String(e.error ?? `Request failed (${res.status})`));
        return;
      }
      const d = res.data as DncBulkResp;
      if (d.platformError) {
        // ONLY an unanswered attempt is worth clicking again. A refusal is deterministic, and
        // telling an operator to retry one is telling her to do something that cannot work.
        if (d.replayable) {
          setPendingBatch(d.batchId);
          setErr(`The platform did not answer for this batch (${d.platformError}). Click again to retry safely: the same batch id replays and writes nothing twice.`);
        } else {
          setPendingBatch('');
          setErr(DNC_WORDING.batchRefused(d.platformError));
        }
        await load();
        return;
      }
      setPendingBatch('');
      setCareLinks(careStopLinksOf(d as DncBulkResp & { careStop?: { members?: Array<{ memberId: string; links?: CareStopLink[] }> } }));
      const sentence = bulkSentence(d);
      if (d.ok) { setOk(sentence); setBulkReason(''); setSelected(new Set()); }
      else setErr(sentence);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy('');
    }
  }

  /** A campaign's name as the platform labels it; the slug when it does not. */
  function campaignName(slug: string): string {
    return campaigns.find((c) => c.slug === slug)?.label || slug.toUpperCase();
  }

  const rows = data?.rows ?? [];
  const superAdmin = data?.superAdmin === true;
  const counts = data?.counts;
  const reason = data?.emptyReason as EmptyReason | undefined;
  const total = data?.total ?? 0;
  const writeOff = data?.platformWrite === false;
  const shownCampaign = campaign && campaign !== 'all' ? campaign : '';
  const issues = Object.entries(data?.issueCounts ?? {}).sort((a, b) => a[0].localeCompare(b[0]));

  function toggleRow(id: string): void {
    setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }

  /** The campaign drop-down a Move or a Mark onto an opt-out needs; the platform's list, nothing else. */
  function campaignPicker(label: string): JSX.Element {
    return (
      <select className="text-input" aria-label={label} value={moveCampaign} onChange={(e) => setMoveCampaign(e.target.value)}>
        <option value="">{campaigns.length ? 'Campaign...' : 'No campaigns named'}</option>
        {campaigns.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
      </select>
    );
  }

  return (
    <div>
      <p className="section-lede">{DNC_WORDING.kindLede[kind]} {DNC_WORDING.kindScope}</p>

      {/* Row 355: the rules she needs to act safely, one neutral line each, no history. */}
      <div className="note" style={{ marginBottom: 14, display: 'grid', gap: 2 }}>
        <div>A row on the list stops every channel. A row marked For review stops nothing.</div>
        <div><strong>Remove</strong> and <strong>Move</strong> need a reason. Nothing is deleted; a removed row stays under Removed.</div>
        <div><strong>Lock</strong> keeps a row from being removed or moved until someone unlocks it. <strong>Adjust</strong> changes the member id, name or note only.</div>
      </div>

      {/* ROW 372: tab 13 only, and only when the deployment offers it. The panel adds a member found
          by the member search; the list below is re-read after every answer so the new row shows. */}
      {kind === 'technical' && features.techExclusion && (
        <TechExclusionPanel platformWrite={features.platformWrite} onDone={load} />
      )}

      {/* ROW 327, now row 354: the TOP-BAR campaign. Only members in it are shown; the store is not
          changed, and a Do not contact row still stops every campaign. */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <span className="micro">Campaign: <strong>{campaign === 'all' ? DNC_WORDING.allCampaigns : campaignName(campaign)}</strong></span>
        <span className="micro muted">(chosen in the top bar)</span>
        {shownCampaign && data && <span className="micro muted">{DNC_WORDING.campaignHides(data.outside ?? null, campaignName(shownCampaign))}</span>}
      </div>

      {data && !data.dncMaster && <div className="note warn" style={{ marginBottom: 14 }}>{DNC_WORDING.notEnabled}</div>}
      {data && data.dncMaster && !data.platformWrite && (
        <div className="note warn" style={{ marginBottom: 14 }}>{DNC_WORDING.platformOff}</div>
      )}
      {reason && reason !== 'no-rows' && (
        <div className={`note ${emptyTone(reason)}`} style={{ marginBottom: 14 }}>
          {emptyNotice(reason, '')}{superAdmin && data?.source ? ` (${data.source})` : ''}
        </div>
      )}
      {data && data.rosterAvailable === false && (
        <div className="note warn" style={{ marginBottom: 14 }}>{DNC_WORDING.fileUnknown}</div>
      )}
      {status === 'locked' && <div className="note" style={{ marginBottom: 14 }}>{DNC_WORDING.lockedView}</div>}
      {staleAt ? <StaleNote at={staleAt} err={err} onRetry={() => void load()} />
        : err && <div className="note danger" style={{ marginBottom: 14 }}>{err}</div>}
      {careLinks.length > 0 && (
        <div className="note" style={{ marginBottom: 14 }} data-testid="care-stop-links">
          {careLinks.map((l, i) => <Fragment key={l.href}>{i > 0 ? ' | ' : ''}<a href={l.href}>{l.label}</a></Fragment>)}
        </div>
      )}
      {ok && <div className="note" style={{ marginBottom: 14 }}>{ok}</div>}

      {/* Tab 13's issues (phone, duplicate, no phone): within the list and the status on screen. */}
      {kind === 'technical' && issues.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }} aria-label="Issue">
          <button type="button" className={issue === '' ? 'chip selected' : 'chip'} onClick={() => { setIssue(''); setOffset(0); }}>Every issue</button>
          {issues.map(([k, n]) => (
            <button key={k} type="button" className={issue === k ? 'chip selected' : 'chip'} onClick={() => { setIssue(k); setOffset(0); }}>
              {DNC_WORDING.issueLabel[k] ?? k} ({n})
            </button>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        {([
          ['active', 'On the list', counts?.active],
          ['locked', 'Locked', counts?.locked],
          ['removed', 'Removed', counts?.removed],
          ['all', 'All', counts ? counts.active + (counts.locked ?? 0) + counts.removed : undefined],
        ] as Array<[DncStatusFilter, string, number | undefined]>).map(([key, label, n]) => (
          <button key={key} type="button" className={status === key ? 'chip selected' : 'chip'} onClick={() => { setStatus(key); setOffset(0); }}>
            {label}{n !== undefined ? ` (${n})` : ''}
          </button>
        ))}
        <form
          onSubmit={(e) => { e.preventDefault(); setQ(typed.trim()); setOffset(0); }}
          style={{ display: 'flex', gap: 4, alignItems: 'center', marginLeft: 8 }}
        >
          <input
            className="text-input"
            aria-label="Search the list"
            placeholder="Name, member id, phone, evidence, or what they said"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            style={{ minWidth: 260 }}
          />
          <button type="submit" className="btn btn-sm btn-ghost">Search</button>
          {q && <button type="button" className="btn btn-sm btn-ghost" onClick={() => { setTyped(''); setQ(''); setOffset(0); }}>Clear</button>}
        </form>
        <span className="micro muted" style={{ marginLeft: 'auto' }}>
          {data?.asOf ? `platform list as of ${fmtTs(data.asOf)}` : ''}
          {data?.dropped ? `, ${data.dropped} row${data.dropped === 1 ? '' : 's'} unreadable` : ''}
        </span>
      </div>

      {/* Row 359 B: a disabled fieldset switches off every button, box and field inside it at once,
          which is how a stale list keeps its rows on screen without any action being taken on them. */}
      <fieldset disabled={!!staleAt} style={LOCK_FIELDSET_STYLE}>
      {/* THE COMMAND BAR (his "multi select ... and then do a command on them"). It appears only with
          a selection, carries ONE reason for the whole batch, and every command confirms in words.
          Row 325: Move, with the campaign drop-down when the target is an opt-out. */}
      {selected.size > 0 && (
        <div className="note" style={{ marginBottom: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <strong>{selected.size} selected</strong>
          <input
            className="text-input"
            aria-label="Reason for the whole batch"
            placeholder="Reason for all of them (required, at least 10 characters)"
            value={bulkReason}
            onChange={(e) => setBulkReason(e.target.value)}
            style={{ minWidth: 320 }}
          />
          {status !== 'locked' && (
            <>
              <button type="button" className="btn btn-sm btn-danger" disabled={busy !== '' || writeOff} title={writeOff ? DNC_WORDING.platformOff : 'Take these rows off the list'} onClick={() => void runBulk('remove')}>
                {pendingBatch ? 'Retry remove' : 'Remove from list'}
              </button>
              <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                <select className="text-input" aria-label="List to move to" value={moveTo} onChange={(e) => setMoveTo(e.target.value as DncKind)}>
                  {DNC_KINDS.filter((k) => k !== kind || k === 'optout').map((k) => <option key={k} value={k}>{DNC_WORDING.kindLabel[k]}</option>)}
                </select>
                {moveTo === 'optout' && campaignPicker('Campaign to opt out of')}
                <button type="button" className="btn btn-sm" disabled={busy !== '' || writeOff} title={writeOff ? DNC_WORDING.platformOff : 'Move these rows to that list'} onClick={() => void runBulk('move')}>Move to</button>
              </span>
              <button type="button" className="btn btn-sm" disabled={busy !== ''} onClick={() => void runBulk('lock')}>Lock</button>
            </>
          )}
          {status !== 'active' && (
            <button type="button" className="btn btn-sm btn-ghost" disabled={busy !== ''} onClick={() => void runBulk('unlock')}>Unlock</button>
          )}
          <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
            <select className="text-input" aria-label="List to mark onto" value={markKind} onChange={(e) => setMarkKind(e.target.value as DncKind)}>
              {DNC_KINDS.map((k) => <option key={k} value={k}>{DNC_WORDING.kindLabel[k]}</option>)}
            </select>
            {markKind === 'optout' && moveTo !== 'optout' && campaignPicker('Campaign the opt-out is for')}
            <label className="micro" style={{ display: 'inline-flex', gap: 3, alignItems: 'center' }}>
              <input type="checkbox" checked={markLock} onChange={(e) => setMarkLock(e.target.checked)} /> and lock
            </label>
            <button type="button" className="btn btn-sm" disabled={busy !== '' || writeOff} title={writeOff ? DNC_WORDING.platformOff : 'Put these people on that list'} onClick={() => void runBulk('mark')}>Mark as</button>
          </span>
          <button type="button" className="btn btn-sm btn-ghost" disabled={busy !== ''} onClick={() => setSelected(new Set())}>Clear selection</button>
          {/* Said plainly rather than discovered: one person holds ONE row, so marking somebody who
              is already listed comes back refused, naming the list they are on. */}
          <div className="micro muted" style={{ flexBasis: '100%' }}>
            Move takes rows from this list to another. Mark as puts people on a list they are not on yet; a row still on a list comes back refused, naming the list it is on. A locked row is refused by Remove and Move alike.
          </div>
        </div>
      )}

      {/* ROW 356: the shared table. PLANNED WIDTHS (walk finding 5) ride each column: left to the
          browser, eleven columns in a 1,144 px page all shrank to their narrowest, so a sentence in any
          one of them stood ten lines tall. The substance takes the rest; a dragged width wins. */}
      <CareGrid
        view={view}
        rows={rows}
        rowKey={(r) => r.dncId}
        noun="rows"
        className="dt dnc-table"
        plannedWidths
        tableWrapStyle={{ overflowX: 'auto' }}
        server={{ facets: data?.facets ?? {}, hidden: data?.sliceHidden ?? 0 }}
        empty={
          // Row 359 B: before the first answer the list says it is loading, never "nobody is on it".
          data === null ? (err ? '' : 'Loading...')
            : emptyRow(reason, status === 'active' ? `Nobody is on the ${DNC_WORDING.kindLabel[kind]} list.` : 'Nothing here.')
        }
      />
      </fieldset>
      {/* The short marks in full, ONCE, rather than the same long sentence on every row (each cell
          also carries its own sentence as a tooltip). */}
      <details className="small muted" style={{ marginTop: 8 }}>
        <summary>What the short marks mean</summary>
        <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '2px 12px', marginTop: 4 }}>
          {FILE_ANSWERS.map((a) => (
            <Fragment key={a}>
              <span className="micro">{DNC_WORDING.fileShort[a]}</span>
              <span>{DNC_WORDING[a]}</span>
            </Fragment>
          ))}
          <span className="micro">{DNC_WORDING.reviewPill}: {DNC_WORDING.reviewShort}</span>
          <span>{DNC_WORDING.reviewNote}</span>
        </div>
      </details>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
        <span className="micro muted">{total ? `${offset + 1} to ${Math.min(offset + PAGE, total)} of ${total}` : ''}</span>
        <button type="button" className="btn btn-sm btn-ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Newer</button>
        <button type="button" className="btn btn-sm btn-ghost" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>Older</button>
      </div>
    </div>
  );
}

/**
 * One nav tab of the Exceptions group (row 325): the heading, and the list. Tabs 10 and 11 also keep
 * the table they had before row 325 (the STOP review and the USPS scans) as a SECOND view under the
 * list, not retired: his call if they should go (the office carried it to him, 8:3x PM).
 */
export function ExceptionsPage({ kind, second }: { kind: DncKind; second?: { label: string; render: () => JSX.Element } }): JSX.Element {
  const [view, setView] = useState<'list' | 'second'>('list');
  const title = DNC_WORDING.kindLabel[kind] ?? kind;
  if (second && view === 'second') {
    return (
      <div>
        <div className="page-pad" style={{ paddingBottom: 0 }}>
          <ViewSwitch view={view} onView={setView} secondLabel={second.label} />
        </div>
        {second.render()}
      </div>
    );
  }
  return (
    <div className="page-pad">
      <div className="section-eyebrow">Member Care &middot; Exceptions</div>
      <h1 className="section-title">{title}</h1>
      <div className="section-rule" />
      {second && <ViewSwitch view={view} onView={setView} secondLabel={second.label} />}
      <ExceptionsList key={kind} kind={kind} />
    </div>
  );
}

function ViewSwitch({ view, onView, secondLabel }: { view: 'list' | 'second'; onView: (v: 'list' | 'second') => void; secondLabel: string }): JSX.Element {
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 10 }} role="tablist" aria-label="View">
      <button type="button" role="tab" aria-selected={view === 'list'} className={view === 'list' ? 'chip selected' : 'chip'} onClick={() => onView('list')}>The list (remove, move, lock)</button>
      <button type="button" role="tab" aria-selected={view === 'second'} className={view === 'second' ? 'chip selected' : 'chip'} onClick={() => onView('second')}>{secondLabel}</button>
    </div>
  );
}

/** Row 359 B: the frame of a tab whose shape waits on the sign-in read, so it is drawn once, not swapped. */
function FeaturesPending(): JSX.Element {
  return (
    <div className="page-pad">
      <div className="section-eyebrow">Member Care &middot; Exceptions</div>
      <div className="muted" style={{ padding: '20px 0' }}>Loading...</div>
    </div>
  );
}

/**
 * Nav 10 and 11 keep the table they had before row 325 as a second view. On a deployment without the
 * master list (MEMBERCARE_DNC_MASTER off) there is no list to show, so the old table IS the tab.
 */
export function OptOutsTab(): JSX.Element {
  const features = useFeatures();
  if (!useFeaturesSettled()) return <FeaturesPending />;
  if (!features.dncMaster) return <OptOutReview />;
  return <ExceptionsPage kind="optout" second={{ label: 'STOP replies to review', render: () => <OptOutReview /> }} />;
}

export function UspsTab(): JSX.Element {
  const features = useFeatures();
  if (!useFeaturesSettled()) return <FeaturesPending />;
  if (!features.dncMaster) return <UspsRejections />;
  return <ExceptionsPage kind="delivery" second={{ label: 'Returned mail', render: () => <UspsRejections /> }} />;
}

/** Nav 12, Do not contact. */
export function DncMaster(): JSX.Element {
  return <ExceptionsPage kind="dnc" />;
}

/** Nav 13, Technical exceptions (row 325, new): no usable phone, bad phones, duplicates, the 28 of row 326. */
export function TechnicalExceptions(): JSX.Element {
  return <ExceptionsPage kind="technical" />;
}
